import { recordedEventsDefinition } from '../lib/recordedEventsDefinition';
import React from 'react';
import PushRegistrationEvidence, { registrationCategories } from './PushRegistrationEvidence';
import { explorerColumns, explorerPresets } from '../lib/userActivityExplorer';
import './UserActivityExplorer.css';

const presetNotes = {
  overview: 'These fields help you inspect missing evidence; no attention score or participation eligibility is inferred. DOB present and Address present are presence checks only.',
  engagement: 'App-load events include web activity. Roster and confirmation records do not establish attendance. ' + recordedEventsDefinition,
  connections: 'Current totals count distinct existing accounts with accepted relationships. Selected-range counts describe retained accepted relationship records created in that range; removed follows are not historical events here.',
  clubs: 'Memberships include accepted and supported legacy records. Hosts are associated through those memberships, not inferred from user roles. Issued invitations do not establish notification delivery.',
};

export default function UserActivityExplorer({
  rows, preset, setPreset, sortKey, direction, onSort, options, observedAt,
}) {
  const definition = explorerPresets[preset];
  const keys = ['display_name', 'username', ...definition.keys];
  const renderValue = (row, key) => {
    if (key === 'registration_details') {
      return <PushRegistrationEvidence report={row.push_registration_report} />;
    }
    const column = explorerColumns[key];
    const value = column.value(row, options);
    if (value === null || value === undefined) return 'Unavailable';
    if (column.type === 'date') {
      return Number.isFinite(Date.parse(value)) ? new Date(value).toLocaleString() : 'Unavailable';
    }
    return value;
  };
  // Registration totals and account counts are deliberately separate units.
  const tiles = preset === 'push'
    ? Object.entries(registrationCategories).map(([key, label]) => ({
      label: label + ' · registrations',
      value: rows.reduce((n, r) => n + (r.push_registration_report?.counts?.[key] || 0), 0),
    }))
    : [{ label: 'Users matching all current filters', value: rows.length }];
  const pushNote = `Eligibility is a read-time observation (${observedAt || 'Unavailable'}), not delivery. Account/device-bound permission reporting is not collected. Registration records do not establish installations or prior delivery.`;

  return (
    <section className="user-explorer" aria-label="User explorer">
      <nav className="explorer-presets" aria-label="Report presets">
        {Object.entries(explorerPresets).map(([key, p]) => (
          <button type="button" key={key} aria-pressed={preset === key} onClick={() => setPreset(key)}>
            {p.title}
          </button>
        ))}
      </nav>
      <h3>{definition.question}</h3>
      <p className="explorer-note">{preset === 'push' ? pushNote : presetNotes[preset]}</p>
      {preset === 'push' && rows.some(r => !r.push_registration_report) && (
        <p>Some registration evidence is unavailable; category totals include available reports only.</p>
      )}
      <div className="explorer-stats">
        {tiles.map(tile => <div key={tile.label}><span>{tile.label}</span><strong>{tile.value}</strong></div>)}
      </div>
      <label className="explorer-mobile-sort">
        Sort by{' '}
        <select aria-label="Sort explorer" value={sortKey} onChange={e => onSort(e.target.value)}>
          {keys.map(key => <option value={key} key={key}>{explorerColumns[key].label}</option>)}
        </select>
        <button type="button" onClick={() => onSort(sortKey)}>
          Order: {direction === 'asc' ? 'ascending' : 'descending'}
        </button>
      </label>
      <div className="explorer-table-scroll">
        <table>
          <thead><tr>
            {keys.map((key, i) => (
              <th key={key} className={i === 0 ? 'explorer-identity' : ''}
                aria-sort={key === sortKey ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}>
                <button type="button" onClick={() => onSort(key)}>
                  {explorerColumns[key].label} {key === sortKey ? (direction === 'asc' ? '↑' : '↓') : '↕'}
                </button>
              </th>
            ))}
          </tr></thead>
          <tbody>
            {rows.map(row => <tr key={row.user_id}>
              {keys.map((key, i) => (
                <td key={key} className={i === 0 ? 'explorer-identity' : ''}>
                  <div className="explorer-cell">{renderValue(row, key)}</div>
                </td>
              ))}
            </tr>)}
          </tbody>
        </table>
      </div>
      <div className="explorer-mobile-cards">
        {rows.map(row => (
          <article key={row.user_id}>
            <h4>{row.display_name || 'Unavailable name'}</h4>
            <dl>{keys.filter(key => key !== 'display_name').map(key => (
              <div key={key}><dt>{explorerColumns[key].label}</dt><dd>{renderValue(row, key)}</dd></div>
            ))}</dl>
          </article>
        ))}
      </div>
      {!rows.length && <p role="status">No users match the current filters.</p>}
    </section>
  );
}
